// externals
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// libraries
import { SlideOut } from '@interstate/components/SlideOut';
import { Box } from '@interstate/components/Box';
import { Button } from '@interstate/components/Button';
import { TextArea } from '@interstate/components/TextArea';
import { TextInput } from '@interstate/components/TextInput';
import { SelectInput } from '@interstate/components/SelectInput';
import { Typography } from '@interstate/components/Typography';
import { Alert } from '@interstate/components/Alert';
import { ExclamationCircleIcon } from '@interstate/components/Icons';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { getDealerEmail, getDealerPhone } from '@makemydeal/dr-shared-store/selectors/dealerSelectors';
import * as bridgeUserSelectors from '@makemydeal/dr-dash-store/selectors/bridgeUserSelectors';
import { getDealXgId, getCommonOrgId, getShopperInfo } from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { reportIssueRSAA, resetReportIssue } from '@makemydeal/dr-dash-store/actions/reportIssueActionCreators';
import {
    getReportIssueIsSubmitting,
    getReportIssueIsSuccess,
    getReportIssueIsFailure
} from '@makemydeal/dr-dash-store/selectors/reportIssueSelectors';
import { validateReportIssueContact } from './reportIssueValidation';

export interface ReportIssueSlideOutProps {
    show: boolean;
    onHide: () => void;
}

const PREFERRED_CONTACT_OPTIONS = [
    { value: 'Email', label: 'Email' },
    { value: 'Phone', label: 'Phone' }
];

const PRIORITY_OPTIONS = [
    { value: 'P4 - Normal - General Question', label: 'P4 - Normal - General Question' },
    { value: 'P3 - Medium - Partial Impairment', label: 'P3 - Medium - Partial Impairment' },
    { value: 'P2 - High - Major Impairment', label: 'P2 - High - Major Impairment' },
    { value: 'P1 - Critical - Total Outage', label: 'P1 - Critical - Total Outage' }
];

export const ReportIssueSlideOutV2: React.FC<ReportIssueSlideOutProps> = ({ show, onHide }) => {
    const dispatch = useDispatch();

    // Selectors
    const dealerId = useSelector(dealerSelectors.getDealerId);
    const selectorDealerName = useSelector(dealerSelectors.getDealerName);
    const selectorDealerEmail = useSelector(getDealerEmail);
    const selectorDealerPhone = useSelector(getDealerPhone);
    const selectorFirstName = useSelector(bridgeUserSelectors.getBridgeFirstName);
    const selectorLastName = useSelector(bridgeUserSelectors.getBridgeLastName);
    const commonOrgId = useSelector(getCommonOrgId);
    const userName = useSelector(bridgeUserSelectors.getFullName);
    const dealXgId = useSelector(getDealXgId);
    const shopperInfo = useSelector(getShopperInfo);
    const isSubmitting = useSelector(getReportIssueIsSubmitting);
    const isSuccess = useSelector(getReportIssueIsSuccess);
    const isFailure = useSelector(getReportIssueIsFailure);
    const isRequiredFieldsEnabled = useSelector(featureToggleSelectors.isReportIssueV2RequiredFieldsEnabled);

    // Contact Information state (pre-populated from selectors)
    const [dealershipName, setDealershipName] = useState(selectorDealerName || '');
    const [firstName, setFirstName] = useState(selectorFirstName || '');
    const [lastName, setLastName] = useState(selectorLastName || '');
    const [email, setEmail] = useState(selectorDealerEmail || '');
    const [phone, setPhone] = useState(selectorDealerPhone || '');

    // Sync selector values into state when they become available after mount
    useEffect(() => {
        if (selectorFirstName) setFirstName(selectorFirstName);
    }, [selectorFirstName]);
    useEffect(() => {
        if (selectorLastName) setLastName(selectorLastName);
    }, [selectorLastName]);
    useEffect(() => {
        if (selectorDealerName) setDealershipName(selectorDealerName);
    }, [selectorDealerName]);
    useEffect(() => {
        if (selectorDealerEmail) setEmail(selectorDealerEmail);
    }, [selectorDealerEmail]);
    useEffect(() => {
        if (selectorDealerPhone) setPhone(selectorDealerPhone);
    }, [selectorDealerPhone]);
    const [preferredContact, setPreferredContact] = useState('Email');

    // Request Details state
    const [subject, setSubject] = useState('');
    const [priority, setPriority] = useState('P4 - Normal - General Question');
    const [description, setDescription] = useState('');
    const [isTouched, setIsTouched] = useState(false);
    const [showValidation, setShowValidation] = useState(false);

    const contactValidation = validateReportIssueContact({
        isRequiredFieldsEnabled,
        preferredContact,
        firstName,
        lastName,
        email,
        phone
    });

    const handleCancel = () => {
        setSubject('');
        setPriority('P4 - Normal - General Question');
        setDescription('');
        dispatch(resetReportIssue());
        setIsTouched(false);
        setShowValidation(false);
        onHide();
    };

    const handleSubmit = () => {
        // Client-side validation: Subject, Message, and Priority must be non-empty
        if (!subject.trim() || !description.trim() || !priority.trim() || !contactValidation.isValid) {
            setShowValidation(true);
            return;
        }

        dispatch(
            reportIssueRSAA({
                // Contact Information
                firstName,
                lastName,
                email,
                phone,
                dealerName: dealershipName,
                accountId: dealerId,
                preferredContact,
                additionalUrls: '',
                additionalContacts: '',
                // Request Details
                subject,
                priority,
                message: description,
                type: 'Defect (Issue)',
                examples: '',
                // NR logging fields (retained for backward compatibility)
                dealerId,
                commonOrgId,
                userName,
                dealXgId,
                customerName: `${shopperInfo?.firstName || ''} ${shopperInfo?.lastName || ''}`.trim()
            })
        );
    };

    const handleDescriptionChange = (e: any) => {
        const value = e.target.value;
        setDescription(value);
        // Mark as touched once user starts typing
        if (!isTouched && value.length > 0) {
            setIsTouched(true);
        }
    };

    const handleDescriptionBlur = () => {
        // Mark as touched on blur if there was any interaction
        if (description.length > 0 || isTouched) {
            setIsTouched(true);
        }
    };

    const showValidationError = isTouched && description.trim().length === 0;

    const errorAlertRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        // istanbul ignore next -- errorAlertRef.current is always set when isFailure is true since they share the same render condition
        if (isFailure && errorAlertRef.current?.scrollIntoView) {
            errorAlertRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [isFailure]);

    const theme = useInterstateTheme();

    return (
        <SlideOut
            data-testid="report-issue-slideout"
            show={show}
            position="right"
            onHide={handleCancel}
            header="Report an Issue"
            panelWidth="480px"
            sx={{
                '.ids-SlideOut-content': {
                    overflowY: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    padding: 0
                }
            }}
            footer={{
                footerComponent: (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 2,
                            backgroundColor: '#f5f5f5',
                            padding: 2
                        }}
                    >
                        {isSuccess ? (
                            <Button buttonStyle="primary" onClick={handleCancel} data-testid="close-button">
                                Close
                            </Button>
                        ) : (
                            <>
                                <Button buttonStyle="tertiary" onClick={handleCancel} data-testid="cancel-button">
                                    Cancel
                                </Button>
                                <Button
                                    buttonStyle="primary"
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                    data-testid="submit-button"
                                >
                                    Submit
                                </Button>
                            </>
                        )}
                    </Box>
                )
            }}
        >
            {isSuccess ? (
                <Box
                    sx={{
                        flexGrow: 1,
                        padding: '1.5rem',
                        display: 'flex',
                        alignItems: 'flex-start'
                    }}
                >
                    <Alert type="success" title="Issue reported successfully" data-testid="success-alert" disableSuccessFade>
                        Thank you, please report this issue to your performance manager so they can keep you updated on progress.
                        This submission will allow us to find the exact replay of your session to understand the situation.
                    </Alert>
                </Box>
            ) : (
                <Box
                    sx={{
                        flexGrow: 1,
                        paddingTop: 0,
                        paddingRight: theme.tokens.BaseSpace60,
                        paddingBottom: theme.tokens.BaseSpace60,
                        paddingLeft: theme.tokens.BaseSpace60,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: theme.tokens.BaseSpace40,
                        overflowY: 'auto'
                    }}
                >
                    <Typography
                        variant="body-sm"
                        sx={{
                            color: theme.tokens.BaseColorGray500,
                            fontSize: theme.tokens.TypeFontSizeSizeMd,
                            fontWeight: theme.tokens.TypeFontWeightWeightMedium
                        }}
                    >
                        This submission will allow us to find the exact replay of your session so no screenshots or recordings are
                        necessary. Please also report this issue to your performance manager so they can keep you updated on
                        progress.
                    </Typography>

                    {/* Contact Information Section */}
                    <Typography variant="h6" data-testid="contact-info-heading">
                        Contact Information
                    </Typography>
                    <TextInput
                        name="dealership-name"
                        label="Dealership Name"
                        value={dealershipName}
                        onChange={(e) => setDealershipName(e.target.value)}
                        data-testid="dealership-name-input"
                    />
                    <TextInput
                        name="first-name"
                        label="First Name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required={contactValidation.required.firstName}
                        hasError={showValidation && contactValidation.missing.firstName}
                        errorMessage={showValidation && contactValidation.missing.firstName ? 'First Name is required' : undefined}
                        data-testid="first-name-input"
                    />
                    <TextInput
                        name="last-name"
                        label="Last Name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required={contactValidation.required.lastName}
                        hasError={showValidation && contactValidation.missing.lastName}
                        errorMessage={showValidation && contactValidation.missing.lastName ? 'Last Name is required' : undefined}
                        data-testid="last-name-input"
                    />
                    <TextInput
                        name="email"
                        label="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required={contactValidation.required.email}
                        hasError={showValidation && contactValidation.missing.email}
                        errorMessage={showValidation && contactValidation.missing.email ? 'Email is required' : undefined}
                        data-testid="email-input"
                    />
                    <TextInput
                        name="phone"
                        label="Phone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        required={contactValidation.required.phone}
                        hasError={showValidation && contactValidation.missing.phone}
                        errorMessage={showValidation && contactValidation.missing.phone ? 'Phone is required' : undefined}
                        data-testid="phone-input"
                    />
                    <SelectInput
                        name="preferred-contact"
                        label="Preferred Method of Contact"
                        value={preferredContact}
                        options={PREFERRED_CONTACT_OPTIONS}
                        onChange={(e) => setPreferredContact(e.target.value as string)}
                        displayDeselectOption={false}
                        data-testid="preferred-contact-select"
                    />

                    {/* Request Details Section */}
                    <Typography variant="h6" data-testid="request-details-heading">
                        Request Details
                    </Typography>
                    <TextInput
                        name="subject"
                        label="Subject"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        hasError={showValidation && !subject.trim()}
                        errorMessage={showValidation && !subject.trim() ? 'Subject is required' : undefined}
                        data-testid="subject-input"
                    />
                    <TextArea
                        name="issue-description"
                        label="Message"
                        placeholder="Details of the issue"
                        value={description}
                        onChange={handleDescriptionChange}
                        onBlur={handleDescriptionBlur}
                        rows={8}
                        data-testid="issue-description-textarea"
                    />
                    {(showValidationError || (showValidation && !description.trim())) && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                marginTop: '-12px'
                            }}
                            data-testid="description-error"
                        >
                            <Box
                                sx={{
                                    color: '#d32f2f',
                                    fontSize: '1rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    lineHeight: 1.66
                                }}
                            >
                                <ExclamationCircleIcon />
                            </Box>
                            <Typography
                                variant="body-sm"
                                sx={{
                                    color: '#d32f2f',
                                    fontSize: '0.75rem',
                                    lineHeight: 1.66
                                }}
                            >
                                Message is required.
                            </Typography>
                        </Box>
                    )}
                    <SelectInput
                        name="priority"
                        label="Priority"
                        value={priority}
                        options={PRIORITY_OPTIONS}
                        onChange={(e) => setPriority(e.target.value as string)}
                        displayDeselectOption={false}
                        hasError={showValidation && !priority.trim()}
                        errorMessage={showValidation && !priority.trim() ? 'Priority is required' : undefined}
                        data-testid="priority-select"
                    />
                    {isFailure && (
                        <div ref={errorAlertRef}>
                            <Alert type="error" title="Failed to report issue" data-testid="error-alert">
                                An error occurred while submitting your report. Please try again.
                            </Alert>
                        </div>
                    )}
                </Box>
            )}
        </SlideOut>
    );
};
